import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CardStepContainer from '../../components/CardStepsContainer/CardStepContainer';
import { User_status, User_status_Dictionary } from '../../components/dictionaries/UserStatus';
import HttpService from '../../services/HttpService';
import { IUserDetails } from '../UserDetails';
import { LocalPhone, Feed, Person, Info, Edit } from '@mui/icons-material';
import { Box, Divider, Grid, Tooltip, Typography, Link, Avatar } from '@mui/material';
import BackButton from '../../components/Buttons/BackButton';
import Theme from '../../Themes/main.theme';
import DetailsCard from '../../components/DetailsCard/DetailsCard';
import { VerticalStep, VerticalStepper } from '../../components/Stepper';
import InfoBoxes from '../../components/InfoBoxes/InfoBoxes';
import MyCalendar from '../../components/Calendar/MyCalendar';
import TableGridWithFilters from '../../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import { getNotesInitialValues } from '../../lib/static/crm/initialValues';
import { getNoteFields } from '../../lib/static/crm/filterFields';
import { notes_columns } from '../../lib/static/crm/columns';
import UserService from '../../services/UserService';
import { IntExt } from '../../lib/permissions';
import { AuthMethod } from '../../lib/generalTypes';
import ActionButton from '../../components/Buttons/ActionButton';
import { useTranslation } from 'react-i18next';
import DialogAction from '../../components/Dialog/DialogAction';
import { useSnackbar } from 'notistack';

function UserProfile() {
  const [data, setData] = React.useState<IUserDetails>({
    id: 0,
    afm: '',
    full_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    profile_picture: '',
    role: '',
    auth: '',
    administrative_unit: '',
    administrative_unit_id: 0,
    is_enabled: false,
    can_be_deleted: false,
    completed_locations: 0,
    completed_professionals: 0,
    completed_applications: 0,
    in_progress_applications: 0,
    notes: [],
    roles: []
  });
  const [notes, setNotes] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [openModal, setOpenModal] = React.useState<boolean>(false);
  const is_internal = UserService.hasRole([IntExt.Internal]);

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();
  const { pathname } = useLocation();

  document.title = t('Το Προφίλ μου');

  const infoDetails = [
    {
      label: <Box display='flex' alignItems='center'>
        {t('Καταχωρίσεις Τοποθεσιών')}
        <Tooltip
          PopperProps={{
            disablePortal: true
          }}
          title={t('Αιτήματα καταχώρισης και αλλαγής στοιχείων τοποθεσιών που του εγκρίθηκαν')}
          placement='bottom-end'>
          <Info sx={{ color: Theme?.iconColor?.main, ml: 1, fontSize: '17px' }} />
        </Tooltip>
      </Box>,
      count: data.completed_locations
    },
    {
      label: <Box display='flex' alignItems='center'>
        {t('Καταχωρίσεις Επαγγελματιών')}
        <Tooltip
          PopperProps={{
            disablePortal: true
          }}
          title={t('Αιτήματα καταχώρισης και αλλαγής στοιχείων επαγγελματιών που του εγκρίθηκαν')}
          placement='bottom-end'>
          <Info sx={{ color: Theme?.iconColor?.main, ml: 1, fontSize: '17px' }} />
        </Tooltip>
      </Box>,
      count: data.completed_professionals
    },
    ...(is_internal
      ? [{
        label: <Box display='flex' alignItems='center'>
          {t('Ολοκληρωμένα Αιτήματα')}
          <Tooltip
            PopperProps={{
              disablePortal: true
            }}
            title={t('Αιτήματα καταχώρισης που Διεκπεραίωσε')}
            placement='top-start'>
            <Info sx={{ color: Theme?.iconColor?.main, ml: 1, fontSize: '17px' }} />
          </Tooltip>
        </Box>,
        count: data.completed_applications
      }]
      : []),
    ...(is_internal
      ? [{
        label: <Box display='flex' alignItems='center'>
          {t('Αιτήματα Σε Επεξεργασία')}
          <Tooltip
            PopperProps={{
              disablePortal: true
            }}
            title={t('Αιτήματα καταχώρισης που είναι υπεύθυνος χειριστής τώρα')}
            placement='top-start'>
            <Info sx={{ color: Theme?.iconColor?.main, ml: 1, fontSize: '17px' }} />
          </Tooltip>
        </Box>,
        count: data.in_progress_applications
      }]
      : [])
  ];

  const user_info_details = [
    {
      name: t('Όνομα'),
      value: data.first_name || '-'
    },
    {
      name: t('Επώνυμο'),
      value: data.last_name || '-'
    }
  ];

  const contact_info_details = [
    {
      name: t('Email'),
      value: data.email || '-'
    },
    {
      name: t('Τηλέφωνο'),
      value: data.phone_number || '-'
    }
  ];
  const user_details = is_internal
    ? [{
      name: t('Ρόλος'),
      value: data.roles.length > 0
        ? data.roles.map((role: any) => role.key).filter(Boolean)
        : '-'
    },
    {
      name: t('Οργανική Μονάδα'),
      value: data.administrative_unit || '-'
    },
    {
      name: t('Κατάσταση'),
      value: data.is_enabled
        ? User_status_Dictionary[User_status.Enabled]
        : User_status_Dictionary[User_status.Disabled]
    }]
    : [{
      name: t('Μέθοδος Πιστοποίησης'),
      value: data.auth || '-'
    },
    ...(data.auth === AuthMethod.BASIC_AUTH
      ? [{
        name: t('Κωδικός'),
        value: <Link onClick={() => setOpenModal(true)} sx={{ '&:hover': { cursor: 'pointer', } }}>
          {t('Αλλαγή Κωδικού')}
        </Link>
      }]
      : [])
    ];

  const steps = [
    ...(!is_internal
      ? [{
        label: t('Στοιχεία Χρήστη'),
        content: <CardStepContainer isLoading={isLoading}
          detailsProps={{ rows: user_info_details, removeLines: true }} />,
        icon: <Person />
      }]
      : []),
    {
      label: t('Στοιχεία Επικοινωνίας'),
      content: <CardStepContainer isLoading={isLoading} detailsProps={{
        rows: contact_info_details,
        removeLines: true
      }} />,
      icon: <LocalPhone />
    },
    {
      label: is_internal
        ? t('Χαρακτηριστικά Στελέχους')
        : t('Χαρακτηριστικά Εισόδου'),
      content: <CardStepContainer isLoading={isLoading} detailsProps={{
        rows: user_details,
        removeLines: true
      }} />,
      icon: <Feed />
    },
  ];

  const onChangePasswordSubmit = async () => {
    try {
      const body = { id: data.id };
      const response = await HttpService.getAxiosClient().post(`${process.env.REACT_APP_BACKEND_URL}/users/password`, body);
      if (response.status === 201) {
        enqueueSnackbar(response.data.message, { variant: 'success' });
      } else {
        // Επειδη μερικές φορές laggarei και ερχονται τα emails από keycloak με καθυστέρηση (πχ 10 λεπτών).
        enqueueSnackbar(response.data.message, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(t('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.'), { variant: 'error' });
      console.error(error);
    }
  };

  const getData = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users/user`);
      setData(response.data);
      is_internal && setNotes(response.data.notes.map((note: any) => {
        return {
          title: note.note,
          start: note.date_note,
          end: note.date_note,
          startAdorment: `${note?.usr_users_crm_notes_created_forTousr_users?.first_name} ${note?.usr_users_crm_notes_created_forTousr_users?.last_name}`
        };
      }));
      setIsLoading(false);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    getData();
  }, []);

  return (
    <>
      <Box mt={2} mb={2}>
        <BackButton onClick={() => navigate(-1)} />
      </Box>
      <Typography variant='h2' color={Theme?.labelColor?.main}>{t('Το Προφίλ μου')}</Typography>
      <Divider sx={{ my: 3, border: 1, color: Theme?.dividerColor?.main }} />
      <Grid container spacing={{ xs: 2, lg: 0 }}>
        <Grid item xs={12}
          lg={6}>
          <DetailsCard
            actionButtons={
              <ActionButton title={t('Επεξεργασία Προφίλ')} icon={<Edit />}
                onClick={() => navigate('edit')} />
            }
            icon={data?.profile_picture
              ? <Avatar alt='My Profile Picture' sx={{ boxShadow: 'rgba(0, 0, 0, 0.35) 0px 5px 15px' }}
                src={data?.profile_picture} />
              : <Person fontSize='large' />}
            title={data.full_name}
            isLoading={isLoading}
            picture={data?.profile_picture}>
            <VerticalStepper activeStep={0}>
              {steps.map(step => {
                return (
                  <VerticalStep
                    active={true}
                    key={step.label}
                    label={step.label}
                    icon={step.icon}>
                    {step.content}
                  </VerticalStep>
                );
              })}
            </VerticalStepper>
          </DetailsCard>
        </Grid>
        <Grid mt={2} item
          xs={12}
          lg={6}>
          <Box display='flex' flexDirection='column'
            alignItems='center' ml={2}>
            <InfoBoxes infoDetails={infoDetails} title={t('Σύνολο Ενεργειών')}
              direction='column' isLoading={isLoading} />
          </Box>
        </Grid>
      </Grid>
      {
        (is_internal && pathname.split('/').filter(Boolean)[0] === 'admin')
          ? <Box mt={4} mb={1}
            height={500} maxWidth='1050'>
            <Typography variant='h3' m={2}> {t('Ημερολόγιο')} </Typography>
            <Divider variant='fullWidth' sx={{ mb: 2 }} />
            <MyCalendar notes={notes} />
            <Box mb={4}>
              <TableGridWithFilters
                title={t('Πίνακας Σημειώσεων')}
                oneLiner={true}
                showCard={false}
                pageSize={15}
                rowsPerPageOptions={[15, 30, 45]}
                sortingMode='server'
                paginationMode='server'
                initialValues={getNotesInitialValues}
                filterFields={getNoteFields}
                columns={notes_columns}
                fetchUrl={`${process.env.REACT_APP_BACKEND_URL}/crm/notes`} />
            </Box>
          </Box>
          : null
      }
      <DialogAction open={openModal} handleClose={() => setOpenModal(false)}
        title={String(t('Αλλαγή Κωδικού'))
        }
        content={t('Θα σας σταλεί ένα ενημερωτικό email για να αλλάξετε τον κωδικό σας. Είστε σίγουρος/η ότι θέλετε να προχωρήσετε;')}
        onAccept={() => {
          onChangePasswordSubmit();
          setOpenModal(false);
        }} />
    </>
  );
}

export default UserProfile;